import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Category as CategoryIcon, Info as InfoIcon } from '@mui/icons-material';
import { authService } from '../services/authService';
import { adInsightsService } from '../services/adInsightsService';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme/tokens';

const CategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const userData = await authService.getCurrentUser();
        const username = userData?.username || userData?.facebook_name || userData?.reddit_username;
        const userId = userData?.id;
        if (!username && !userId) { setError('Username not found. Please log in again.'); return; }
        const result = await adInsightsService.getAdInsights({ username, userId });
        if (result.status === 'success') {
          setCategories(result.data?.fb_other_categories || []);
        } else {
          setError(result.message || 'Failed to load categories');
        }
      } catch (err) {
        console.error('Load categories error:', err);
        if (err.response?.status === 401 || err.response?.status === 403) navigate('/login');
        else setError('Failed to load categories data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>Loading categories...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, pb: 6 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
          borderRadius: `${tokens.radius.card}px`,
          background: tokens.hero.gradient,
          border: tokens.hero.border,
          boxShadow: tokens.hero.shadow,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', border: `1px solid ${tokens.border.subtle}` }}>
              <CategoryIcon sx={{ color: tokens.text.primary, fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.text.primary, letterSpacing: '-0.02em' }}>
                Categories
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.text.secondary, mt: 0.25 }}>
                Interest-like categories advertisers use to reach you
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/dashboard')}
            sx={{ bgcolor: tokens.accent.brandBlue, color: '#fff', fontWeight: 600, '&:hover': { bgcolor: '#1D4ED8' } }}
          >
            Back to Dashboard
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {categories.length === 0 && !error && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <InfoIcon sx={{ fontSize: 54, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>No categories found</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload your Facebook DYI ZIP on the Facebook page to see other categories used to reach you.
          </Typography>
        </Paper>
      )}

      {categories.length > 0 && (
        <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Other categories used to reach you</Typography>
              <Chip label={categories.length} color="secondary" size="small" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Interest-like categories advertisers can use to target people similar to you.
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default CategoriesPage;
